import { useState, useEffect, useRef } from "react";
import { useConversationStore } from "../../stores/conversationStore";
import { useUserStore } from "../../stores/userStore";
import { useMessageStore } from "../../stores/messageStore";
import { useSettingsStore } from "../../stores/settingsStore";
import { conversationAPI } from "../../services/api";
import { unifiedMessageService } from "../../services/unifiedMessageService";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { Style } from "../../services/api";
import { HeroScreen } from "./HeroScreen";
import { v4 as uuidv4 } from 'uuid';
import { projectInitializationService } from "../../services/projectInitializationService";
import { AppShell } from "../layout/AppShell";
import { useAppShell } from "../layout/useAppShell";


interface ChatLayoutProps {
  defaultView?: "gallery" | "slide" | "chat";
  selectedSlideHash?: string;
  selectedSlidePath?: string | null;
}

export function ChatLayout({
  defaultView,
  selectedSlidePath: propSelectedSlidePath,
}: ChatLayoutProps = {}) {
  const [viewMode, setViewMode] = useState<"code" | "preview">("preview");
  const [selectedSlidePath, setSelectedSlidePath] = useState<string | null>(
    propSelectedSlidePath || null,
  );
  const [activePreviewTab, setActivePreviewTab] = useState("slides");
  const [showHero, setShowHero] = useState(!defaultView);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [chatSidebarWidth] = useState(() => {
    // Get saved width from localStorage or use default
    const saved = localStorage.getItem("chatSidebarWidth");
    return saved ? parseInt(saved, 10) : 320;
  });

  // Ref to track pending message data for after navigation
  const pendingMessageRef = useRef<any>(null);

  const { setActiveConversation, addConversation, conversations } =
    useConversationStore();
  const { activeOrganization, activeTeam } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on a conversation route - use location from router
  const isOnConversationRoute = location.pathname.startsWith("/chat/");
  const isOnSlideRoute = location.pathname.includes("/slide/");


  // Save width to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("chatSidebarWidth", chatSidebarWidth.toString());
  }, [chatSidebarWidth]);

  // Show hero screen when on home route (not a conversation route)
  // Since conversations are now on separate routes (/chat/$id/*),
  // ChatLayout at "/" should always show the hero screen
  useEffect(() => {
    if (isOnConversationRoute) {
      // On a conversation route - hide hero (though this shouldn't happen
      // since conversations use their own layouts now)
      setShowHero(false);
      setIsTransitioning(false);
    } else {
      // On home route - always show hero immediately
      // No need for transition logic since we're not switching between hero/chat
      setShowHero(true);
      setIsTransitioning(false);
    }
  }, [isOnConversationRoute]);

  // Watch for conversation changes to send pending message after navigation completes
  useEffect(() => {
    const sendPendingMessage = async () => {
      // Check if we have a pending message and we're on the correct conversation route
      if (pendingMessageRef.current && isOnConversationRoute) {
        const pendingConversationId = pendingMessageRef.current.conversationId;

        // Extract conversation ID from the current route
        const pathParts = location.pathname.split('/');
        const routeConversationId = pathParts[2]; // /chat/{conversationId}/...

        // Ensure we're on the correct conversation page
        if (routeConversationId !== pendingConversationId) {
          console.log('⏳ Waiting for correct conversation route...', { routeConversationId, pendingConversationId });
          return;
        }

        console.log('🚀 Navigation completed, preparing to send first message...');

        // Wait for the conversation to be properly set as active
        // We'll retry a few times with delays to ensure proper setup
        let retries = 0;
        const maxRetries = 10;
        const retryDelay = 100;

        while (retries < maxRetries) {
          // Check if the conversation is now active
          const currentActiveId = useConversationStore.getState().activeConversationId;

          if (currentActiveId === pendingConversationId) {
            console.log('✅ Conversation is now active, proceeding with message send');
            break;
          }

          console.log(`⏳ Waiting for conversation to become active (attempt ${retries + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          retries++;

          // On the last retry, force set the active conversation if needed
          if (retries === maxRetries - 1 && currentActiveId !== pendingConversationId) {
            console.log('⚠️ Force setting active conversation after max retries');
            setActiveConversation(pendingConversationId);
          }
        }

        // Wait for ChatArea component to be ready
        console.log('⏳ Waiting for ChatArea component to be ready...');
        const chatAreaReady = await new Promise<boolean>((resolve) => {
          let timeout: NodeJS.Timeout;

          const handleChatAreaReady = (event: Event) => {
            const customEvent = event as CustomEvent;
            if (customEvent.detail?.conversationId === pendingConversationId) {
              console.log('✅ ChatArea component is ready');
              clearTimeout(timeout);
              window.removeEventListener('chat-area-ready', handleChatAreaReady);
              resolve(true);
            }
          };

          // Listen for the ready event
          window.addEventListener('chat-area-ready', handleChatAreaReady);

          // Set a timeout to proceed anyway after 2 seconds
          timeout = setTimeout(() => {
            console.log('⚠️ ChatArea ready timeout, proceeding anyway');
            window.removeEventListener('chat-area-ready', handleChatAreaReady);
            resolve(false);
          }, 2000);
        });

        if (!chatAreaReady) {
          console.warn('⚠️ ChatArea may not be fully ready, but proceeding with message send');
        }

        // Small additional delay to ensure all components are settled
        await new Promise(resolve => setTimeout(resolve, 100));

        console.log('📤 Components ready, sending first message now...');

        try {
          await unifiedMessageService.sendMessage(pendingMessageRef.current);
          console.log('✅ Pending message sent successfully');
        } catch (error) {
          console.error('❌ Failed to send pending message:', error);
        } finally {
          // Clear pending message and end transition
          pendingMessageRef.current = null;
          setIsTransitioning(false);
        }
      }
    };

    sendPendingMessage();
  }, [isOnConversationRoute, location.pathname, setActiveConversation]);

  // Auto-send message for slide workflow (from hero page)
  useEffect(() => {
    const handleAutoSendForSlideWorkflow = async () => {
      const locationState = location.state as any;

      // Check if auto-send is triggered and we have user instructions
      if (!locationState?.autoSend || !locationState?.userInstructions) {
        return;
      }

      // Check if we're on a conversation route
      if (!isOnConversationRoute) {
        return;
      }

      // Skip auto-send for VIDEO conversations - VideoWorkspaceShell handles those
      if (location.pathname.includes('/video-workspace')) {
        console.log('⏭️ Skipping ChatLayout auto-send for video-workspace (handled by VideoWorkspaceShell)');
        return;
      }

      // Extract conversation ID from the current route
      const pathParts = location.pathname.split('/');
      const routeConversationId = pathParts[2]; // /chat/{conversationId}/...

      if (!routeConversationId) {
        return;
      }

      console.log('🎬 Auto-send triggered for slide workflow:', {
        conversationId: routeConversationId,
        instructions: locationState.userInstructions,
        provider: locationState.provider,
      });

      // Wait for the conversation to be properly set as active
      let retries = 0;
      const maxRetries = 10;
      const retryDelay = 100;

      while (retries < maxRetries) {
        const currentActiveId = useConversationStore.getState().activeConversationId;

        if (currentActiveId === routeConversationId) {
          console.log('✅ Conversation is active, sending auto-message');
          break;
        }

        console.log(`⏳ Waiting for conversation to become active (attempt ${retries + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        retries++;

        if (retries === maxRetries - 1 && currentActiveId !== routeConversationId) {
          console.log('⚠️ Force setting active conversation');
          setActiveConversation(routeConversationId);
        }
      }

      // Format the message
      const formattedMessage = `Create the slide from the attached template and user instructions: ${locationState.userInstructions}`;

      // Get the selected model from settingsStore
      const { currentModel } = useSettingsStore.getState();

      console.log('📤 Sending auto-message:', formattedMessage);

      try {
        await unifiedMessageService.sendMessage({
          conversationId: routeConversationId,
          content: formattedMessage,
          model: currentModel,
          provider: locationState.provider,
        });
        console.log('✅ Auto-message sent successfully');
      } catch (error) {
        console.error('❌ Failed to send auto-message:', error);
      }

      // Clear the state to prevent re-sending on refresh
      navigate({
        to: location.pathname as any,
        replace: true,
        state: {},
      });
    };

    handleAutoSendForSlideWorkflow();
  }, [isOnConversationRoute, location.pathname, location.state, setActiveConversation, navigate]);

  // Update slide path from props when on slide route, clear when on gallery route
  useEffect(() => {
    if (
      isOnSlideRoute &&
      propSelectedSlidePath &&
      propSelectedSlidePath !== selectedSlidePath
    ) {
      setSelectedSlidePath(propSelectedSlidePath);
      if (viewMode !== "preview") {
        setViewMode("preview");
      }
      if (activePreviewTab !== "slides") {
        setActivePreviewTab("slides");
      }
    } else if (!isOnSlideRoute && selectedSlidePath) {
      // Clear selected slide when not on slide route (i.e., on gallery route)
      setSelectedSlidePath(null);
    }
  }, [
    isOnSlideRoute,
    propSelectedSlidePath,
    selectedSlidePath,
    viewMode,
    activePreviewTab,
    location.pathname,
  ]);

  // Helper function to convert ArrayBuffer to base64
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const chunkSize = 0x8000; // 32KB chunks to avoid stack overflow

    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode(...chunk);
    }

    return btoa(binary);
  };

  /**
   * NOTE: This function is no longer used - HeroScreen now navigates directly after analysis
   * Keeping for backwards compatibility, can be removed in future cleanup
   */
  // @ts-ignore - Function kept for future use
  const _handleStartChat = async (
    message: string,
    model: string,
    attachedFiles?: any[],
    selectedStyle?: Style,
    conversationType:
      | "VIDEO"
      | "STYLE" = "VIDEO",
    existingConversationId?: string,
  ) => {
    try {
      // Check if organization and team are selected
      if (!activeOrganization?.id || !activeTeam?.id) {
        console.error(
          "Organization and team must be selected to start a conversation",
        );
        return;
      }

      // Start transition
      setIsTransitioning(true);

      let newConversation;

      // Use existing conversation if provided, otherwise create new one
      if (existingConversationId) {
        console.log('📁 Using existing conversation:', existingConversationId);
        // Find existing conversation from store
        newConversation = conversations.find((c: any) => c.id === existingConversationId);
        if (!newConversation) {
          throw new Error('Existing conversation not found');
        }

        // Update conversation with current model selection
        // This ensures we use the user's current choice, not what was stored
        if (model && newConversation.modelName !== model) {
          console.log(`🔄 Updating conversation model from ${newConversation.modelName} to ${model}`);
          try {
            await conversationAPI.update(existingConversationId, {
              model: model,
              styleId: selectedStyle?.id
            });
            // Update local conversation object with new model
            newConversation = { ...newConversation, modelName: model };
          } catch (error) {
            console.warn('⚠️ Failed to update conversation model, using stored value:', error);
          }
        }
      } else {
        // Create new conversation with required org/team and styleId
        const response = await conversationAPI.create({
          title: message.slice(0, 50) + (message.length > 50 ? "..." : ""),
          model,
          organizationId: activeOrganization.id,
          teamId: activeTeam.id,
          styleId: selectedStyle?.id,
          collectionType: conversationType,
        });

        newConversation = response.conversation;

        // Add to conversation store
        addConversation(newConversation);

        // Initialize project structure for video conversations
        if (conversationType === "VIDEO" && newConversation?.id) {
          console.log('🎬 Initializing video project structure for new conversation...');
          try {
            await projectInitializationService.initializeProject({
              conversationId: newConversation.id,
              workingDirectory: undefined, // Backend determines path
              styleId: selectedStyle?.id,
              initGit: true,
              retryOnGitError: true
            });
            console.log('✅ Project initialized successfully');
          } catch (error) {
            console.error('⚠️ Project initialization failed:', error);
            // Don't fail the entire flow if project init fails
          }
        }
      }

      // Set as active conversation
      setActiveConversation(newConversation.id);

      // CRITICAL FIX: Create placeholder thinking immediately for better UX
      // This shows instant feedback even before navigation completes
      const placeholderMessageId = uuidv4();

      // Generate contextual placeholder subjects based on the user's message
      let placeholderThinkingSubjects = [
        'Analyzing your request',
        'Understanding context',
        'Processing information'
      ];

      const msgLower = message.toLowerCase();

      // Detect type of request and customize placeholders
      if (msgLower.includes('video') || msgLower.includes('animation') || msgLower.includes('explainer')) {
        placeholderThinkingSubjects = [
          'Understanding video requirements',
          'Planning visual narrative',
          'Preparing content structure'
        ];
      } else if (msgLower.includes('slide') || msgLower.includes('presentation')) {
        placeholderThinkingSubjects = [
          'Analyzing slide requirements',
          'Reviewing presentation structure',
          'Preparing visual elements'
        ];
      } else if (msgLower.includes('image') || msgLower.includes('visual') || msgLower.includes('picture')) {
        placeholderThinkingSubjects = [
          'Understanding visual requirements',
          'Preparing image generation',
          'Analyzing design parameters'
        ];
      } else if (msgLower.includes('style') || msgLower.includes('design') || msgLower.includes('theme')) {
        placeholderThinkingSubjects = [
          'Analyzing style preferences',
          'Understanding design requirements',
          'Preparing style configuration'
        ];
      }

      // Create user message immediately
      const userMessageId = uuidv4();
      const userMessage = {
        id: userMessageId,
        conversationId: newConversation.id,
        role: 'user' as const,
        content: message,
        timestamp: new Date(),
        attachments: [],
        metadata: {
          streamComplete: true,
        },
        status: 'sent' as const,
        thinkingHistory: [],
        currentThinkingSubject: undefined,
        currentThinking: undefined,
      };

      // Create placeholder AI message for instant feedback
      const placeholderAIMessage = {
        id: placeholderMessageId,
        conversationId: newConversation.id,
        role: 'assistant' as const,
        content: '',
        timestamp: new Date(),
        attachments: [],
        metadata: {
          streamComplete: false,
          isPlaceholder: true, // Mark as placeholder to be replaced
        },
        status: 'streaming' as const,
        thinkingHistory: [
          {
            subject: placeholderThinkingSubjects[0],
            timestamp: Date.now(),
            completed: false
          }
        ],
        currentThinkingSubject: placeholderThinkingSubjects[0],
        currentThinking: 'Initializing AI processing and analyzing your request...',
      };

      console.log('🎯 Creating immediate placeholder for instant feedback before navigation');

      // Add messages to store immediately
      useMessageStore.getState().addMessage(userMessage);
      useMessageStore.getState().addMessage(placeholderAIMessage);
      useMessageStore.getState().setStreamingMessage(placeholderMessageId);

      // Store the placeholder ID globally so it can be replaced when real message arrives
      (window as any).__placeholderMessageId = placeholderMessageId;

      // Add more placeholders progressively to show activity
      placeholderThinkingSubjects.slice(1).forEach((subject, index) => {
        setTimeout(() => {
          const currentMsg = useMessageStore.getState().getMessageById(placeholderMessageId);
          // Only add placeholder if still a placeholder and not replaced
          if (currentMsg && currentMsg.metadata?.isPlaceholder) {
            useMessageStore.getState().addThinkingSubject(placeholderMessageId, subject);
          }
        }, (index + 1) * 800); // Stagger placeholders every 800ms
      });

      // CRITICAL FIX: Navigate first, then send message after navigation completes
      // This ensures chat components are mounted before SSE stream starts

      // Prepare the message data first (but with placeholderMessageId and userMessageId)
      const sendMessageData: any = {
        conversationId: newConversation.id,
        content: message,
        model,
        placeholderMessageId, // Pass the pre-created placeholder ID
        skipUserMessage: true, // Skip user message creation since we already created it
      };

      // Add context for VIDEO conversations so the worker routes to video_outline_system.md
      if (conversationType === "VIDEO") {
        sendMessageData.context = {
          source: 'outline',
          outlineAction: 'create',
        };
      }

      // Add file attachments if any
      if (attachedFiles && attachedFiles.length > 0) {
        const images = attachedFiles
          .filter((file) => file.type === "image" || file.type === "pdf")
          .map((file) => ({
            data: arrayBufferToBase64(file.data),
            mimeType: file.mimeType,
            filename: file.file.name,
          }));

        if (images.length > 0) {
          sendMessageData.images = images;
        }
      }

      // Add selected style information if provided
      if (selectedStyle) {
        sendMessageData.selectedStyle = {
          id: selectedStyle.id,
          name: selectedStyle.name,
          description: selectedStyle.description,
        };
      }

      // Store message data to send after navigation completes
      pendingMessageRef.current = sendMessageData;

      // Hide hero screen before navigation
      setShowHero(false);

      // Navigate to the new conversation - message will be sent automatically
      // via the useEffect hook once navigation and mounting are complete
      await navigate({
        to: "/chat/$conversationId/gallery",
        params: { conversationId: newConversation.id },
        search: { slide: undefined, tab: undefined },
      });

      // Note: setIsTransitioning(false) is called in the useEffect after message is sent

    } catch (error) {
      console.error("Failed to start chat:", error);
      setIsTransitioning(false);
    }
  };

  return (
    <AppShell>
      <div
        className={showHero ? "h-full bg-background overflow-hidden" : "flex h-full bg-background relative overflow-hidden"}
      >
        {showHero && (
          <div className="w-full h-full overflow-y-auto [scrollbar-gutter:stable]">
            <ShellHero isTransitioning={isTransitioning} hasActiveTeam={!!activeTeam} />
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ShellHero(props: { isTransitioning: boolean; hasActiveTeam: boolean }) {
  const shell = useAppShell();
  return <HeroScreen {...props} onToggleSidebar={shell?.isPhone ? shell.openNav : undefined} showLogo={shell ? shell.logoInBar : true} />;
}
